import React, { useState, useContext, useRef } from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { useQuery } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import { mapAfter } from 'apollo-relay/update-query';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { useScroll } from 'lib/scroll/use-scroll';

import circleArrow from 'four-nets/icons/svg/icon_arrow-collapse.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { ViewerContext } from 'four-nets/user/viewer/viewer';

import UserBrand from './user-brand';
import CreateUserBrand from '../create-user-brand/create-user-brand';

import query, { UserBrandsQuery, UserBrandsQueryVariables } from './user-brands.graphql';
import defaultStyles from './user-brands.scss';

export interface Props {
    styles: typeof defaultStyles;
    username: string;
}

const UserBrands: React.FC<Props> = React.memo(props => {
    const { styles, username } = props;

    const ref = useRef(null);
    const scrollToRef = useScroll();
    const { viewer } = useContext(ViewerContext);

    const [collapsed, setCollapsed] = useState(false);
    const [open, setOpen] = useState(false);

    const { data, refetch: refetchQuery, loading, fetchMore } = useQuery<UserBrandsQuery, UserBrandsQueryVariables>(
        query,
        {
            variables: { username },
            fetchPolicy: 'cache-first',
        }
    );

    const isAuthor = viewer && viewer.username === username;
    const viewerIsVendor = Boolean(viewer && viewer.profile && viewer.profile.vendor);

    const totalCount = (data && data.userBrands && data.userBrands.totalCount) || 0;
    const endCursor = data && data.userBrands && data.userBrands.pageInfo && data.userBrands.pageInfo.endCursor;
    const hasNextPage = data && data.userBrands && data.userBrands.pageInfo && data.userBrands.pageInfo.hasNextPage;
    const fetchMoreFn = hasNextPage
        ? () => {
              return fetchMore({
                  variables: { after: endCursor },
                  updateQuery: (
                      previousResult = {} as UserBrandsQuery,
                      { fetchMoreResult = {} as UserBrandsQuery }: { fetchMoreResult?: UserBrandsQuery }
                  ) => {
                      return { userBrands: mapAfter(previousResult.userBrands, fetchMoreResult.userBrands) };
                  },
              });
          }
        : undefined;

    const refetch = () => {
        refetchQuery().then(() => {
            scrollToRef(ref.current, 100);
        });
    };

    return (
        <>
            {data && data.userBrands && data.userBrands.edges && (data.userBrands.edges.length || isAuthor) ? (
                <div ref={ref} className={classnames(styles.wrapper, { [styles.collapsed]: collapsed })}>
                    <LoadingWrapperView loading={loading}>
                        <button
                            onClick={() => {
                                setCollapsed(!collapsed);
                            }}
                            className={styles.heading}
                        >
                            <FormattedMessage id='I_RECOMMEND_SERVICES' values={{ count: totalCount || '' }} />
                            {totalCount ? (
                                <div
                                    className={classnames(styles.iconWrapper, {
                                        [styles.collapsed]: collapsed,
                                    })}
                                >
                                    <SVGIcon style={{ image: styles.icon }} src={circleArrow} />
                                </div>
                            ) : null}
                        </button>
                        <div className={styles.userBrands}>
                            {data.userBrands.edges.map(edge =>
                                edge && edge.node ? (
                                    <UserBrand
                                        isAuthor={Boolean(isAuthor)}
                                        username={username}
                                        refetch={refetch}
                                        {...edge.node}
                                    />
                                ) : null
                            )}
                            {hasNextPage && !open ? (
                                <div className={styles.buttons}>
                                    <button
                                        disabled={loading}
                                        className={styles.button}
                                        onClick={() => {
                                            if (fetchMoreFn) {
                                                fetchMoreFn();
                                            }
                                        }}
                                    >
                                        <FormattedMessage id='BUTTON_SHOW_MORE' />
                                    </button>
                                </div>
                            ) : null}
                            {isAuthor && !viewerIsVendor && !open ? (
                                <div className={styles.buttons}>
                                    <button
                                        onClick={() => {
                                            setOpen(true);
                                        }}
                                        className={classnames(styles.button, styles.add)}
                                    >
                                        <FormattedMessage id='ADD_RECOMMENDATION_SUBMIT' values={{ gender: 'v' }} />
                                    </button>
                                </div>
                            ) : null}
                            <CreateUserBrand open={open} close={() => setOpen(false)} refetch={refetch} />
                        </div>
                    </LoadingWrapperView>
                </div>
            ) : null}
        </>
    );
});

export default Styled(defaultStyles)(UserBrands);
